import React from 'react';
import { cva } from 'class-variance-authority';
import { observer } from 'src/utils/mobx-react';
import { cn } from 'src/domains/common/components/utils';
import { iconComponent } from './alert-utils';
import { AlertPropsType } from './alert.types';

export const alertVariants = cva(
    'relative w-full rounded-lg border border-solid border-gray-200 px-4 py-3 text-sm grid has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
    {
        variants: {
            variant: {
                default: 'bg-card text-card-foreground',
                destructive:
                    'text-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    }
);

const AlertWrapper = observer('Alert', ({ variant, children }: AlertPropsType) => {
    return <div className={cn(alertVariants({ variant }))}>{children}</div>;
});

const AlertTitle = observer('AlertTitle', ({ children }: React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            data-slot='alert-title'
            className={cn('col-start-2 line-clamp-1 min-h-4 font-semibold tracking-tight')}
        >
            {children}
        </div>
    );
});

const AlertDescription = observer('AlertDescription', ({ children }: React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            data-slot='alert-description'
            className={cn('col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed')}
        >
            {children}
        </div>
    );
});

export const Alert = observer('Alert', ({ children, variant, title, icon, ...props }: AlertPropsType) => {
    return (
        <AlertWrapper
            variant={variant}
            {...props}
        >
            {iconComponent(icon)}
            <AlertTitle>{title}</AlertTitle>
            <AlertDescription>{children}</AlertDescription>
        </AlertWrapper>
    );
});
